import { useState } from "react";
import { Copy, RefreshCw, UserRoundPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMe } from "@/hooks/queries/use-me";
import { useToolboxAccess, useRoleGranters } from "@/hooks/queries/use-toolbox-access";
import { joinNames } from "@/lib/toolbox/join-names";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { useAuthStore } from "@/stores/auth-store";

/** How often the page re-checks for a role (frame 1d note: "checks again on its own every minute"). */
export const ROLE_POLL_MS = 60_000;

/** Setup's lead line when the person has no role yet (frame 1d). */
export const NO_ROLE_LEAD = "Almost there. The last step needs a role on a project.";

/** The ready-made message to send (frame 1d), with the person's account. Pure; exported for tests. */
export function askForRoleMessage(email: string): string {
  return `Hi, could you give me a SpecTree Toolbox role on our Azure DevOps project? I've finished setup. My account is ${email}.`;
}

export interface AskForRoleProps {
  /** The page's "Check again" (setup, access and the PAT); without it, only the access is re-read. */
  onCheckAgain?: () => void;
  /** True while that re-check runs. */
  checking?: boolean;
}

/**
 * "Ask for a role" (frame 1d; 03-evaluation.md section 8.6 Q8, UX-PM-42): in
 * place of a Continue that would stay disabled for ever, it names who can
 * grant a role (the project's Project Manager or an Admin, with the Admins
 * from `GET /toolbox/access/granters`) and offers a message with the
 * person's account to copy. The Toolbox access is re-read every minute while
 * it shows, and on "Check again"; when a role lands the page swaps it for
 * Continue (and `useMeFollowsToolboxAccess` re-reads `/me`, which opens the
 * Grooming pages).
 */
export function AskForRole({ onCheckAgain, checking = false }: AskForRoleProps = {}) {
  const access = useToolboxAccess({ refetchInterval: ROLE_POLL_MS });
  const granters = useRoleGranters();
  const { data: me } = useMe();
  const authEmail = useAuthStore((state) => state.user?.email);
  const email = me?.email ?? authEmail ?? "";
  const message = askForRoleMessage(email);
  const [copied, setCopied] = useState<"yes" | "failed" | null>(null);

  const admins = joinNames((granters.data?.admins ?? []).map((admin) => admin.name), "and");
  const busy = checking || access.isFetching;
  const checkAgain = () => {
    if (onCheckAgain) onCheckAgain();
    else void access.refetch();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied("yes");
    } catch {
      setCopied("failed");
    }
  };

  return (
    <section
      className="mt-[18px] flex gap-[18px] rounded-[10px] border border-[#1F1F23] bg-[#111113] px-5 py-[18px]"
      aria-labelledby="ask-for-role-title"
      data-testid="ask-for-role"
    >
      <UserRoundPlus className="h-[22px] w-[22px] shrink-0 text-[#6CC4FF]" aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        <h2 id="ask-for-role-title" className="font-display text-base font-semibold">
          Ask for a role
        </h2>
        <p className={cn(TOOLBOX_TEXT.content, "leading-relaxed text-muted-foreground")} data-testid="ask-for-role-who">
          You don&apos;t have a Toolbox role yet. Your project&apos;s Project Manager or an Admin can give you one.
          {admins ? (
            <>
              {" "}
              Admins: <b className="font-semibold text-foreground">{admins}</b>.
            </>
          ) : null}
        </p>
        <p
          className={cn(TOOLBOX_TEXT.content, "rounded-lg border border-[#1F1F23] bg-[#0B0B0D] px-3 py-2.5 leading-relaxed text-[#D4D4D8]")}
          data-testid="ask-for-role-message"
        >
          {message}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
            onClick={() => void copy()}
          >
            <Copy aria-hidden="true" />
            {copied === "yes" ? "Copied" : "Copy this message"}
          </Button>
          <Button variant="outline" size="sm" onClick={checkAgain} disabled={busy}>
            <RefreshCw className={cn(busy && "animate-spin")} aria-hidden="true" />
            Check again
          </Button>
          {copied === "failed" ? (
            <span className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} role="status">
              Couldn&apos;t copy. Select the message and copy it yourself.
            </span>
          ) : null}
        </div>
      </div>
    </section>
  );
}
